import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, FileText, Search, CalendarClock, AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/adminApi";
import { INDUSTRIA_LABELS } from "@/components/admin/LeadsManager";
import { EMAILS_ENABLED } from "@/constants/site";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("es-CL"); } catch { return iso || "-"; }
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.floor((d - new Date()) / (1000 * 60 * 60 * 24));
};

export function ActivePoliciesManager({ leads, onChange }) {
  const [q, setQ] = useState("");
  const [sending, setSending] = useState({});

  const activePolicies = useMemo(() => {
    return (leads || [])
      .filter((l) => l.status === "ganado" && !l.archived && l.poliza)
      .sort((a, b) => (a.poliza_vence || "").localeCompare(b.poliza_vence || ""));
  }, [leads]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return activePolicies;
    return activePolicies.filter((l) => (
      [l.nombre, l.rut, l.email, l.aseguradora, l.equipo_descripcion, ...(l.tipo_industria || [])].join(" ").toLowerCase().includes(term)
    ));
  }, [activePolicies, q]);

  const sendPoliza = async (l) => {
    setSending((s) => ({ ...s, [l.id]: true }));
    try { await api.post(`/admin/leads/${l.id}/send-poliza`); toast.success("Póliza enviada"); onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo"); }
    finally { setSending((s) => ({ ...s, [l.id]: false })); }
  };

  const expiring = activePolicies.filter((l) => {
    const d = daysUntil(l.poliza_vence);
    return d !== null && d <= 30;
  });

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display text-3xl font-bold text-brand-ink">Seguros Activos</h2>
        <p className="mt-1 text-sm text-gray-500">Clientes con póliza contratada. Revisa los próximos a vencer para renovarlos a tiempo.</p>
      </div>
      <div className={`grid grid-cols-1 gap-4 mb-6 ${EMAILS_ENABLED ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{activePolicies.length}</p>
          <p className="mt-1 text-sm text-gray-500">Pólizas activas</p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <CalendarClock className="h-5 w-5" />
          </div>
          <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{expiring.length}</p>
          <p className="mt-1 text-sm text-gray-500">Vencen en 30 días</p>
        </div>
        {EMAILS_ENABLED && (
          <div className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
              <Mail className="h-5 w-5" />
            </div>
            <p className="mt-4 font-display text-3xl font-bold text-brand-ink">{activePolicies.filter((l) => l.poliza_enviada_at).length}</p>
            <p className="mt-1 text-sm text-gray-500">Pólizas enviadas por correo</p>
          </div>
        )}
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input data-testid="policies-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, RUT, aseguradora..." className="pl-9" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <ShieldCheck className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          Sin pólizas activas.
        </div>
      ) : (
        <>
        {/* Mobile: tarjetas */}
        <div className="space-y-3 md:hidden" data-testid="policies-cards-mobile">
          {filtered.map((l) => {
            const d = daysUntil(l.poliza_vence);
            const soon = d !== null && d <= 30;
            const overdue = d !== null && d < 0;
            return (
              <div key={l.id} data-testid={`policy-card-${l.id}`} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="font-semibold text-brand-ink">{l.nombre}</p>
                <p className="text-xs text-gray-500">{l.rut} · {l.email}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(l.tipo_industria || []).map((k) => (
                    <span key={k} className="inline-flex items-center rounded-full bg-brand-yellow/15 px-2 py-0.5 text-[11px] font-semibold text-brand-ink">
                      {INDUSTRIA_LABELS[k] || k}
                    </span>
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 text-xs">
                  <span className="text-gray-500">{l.aseguradora || "Sin aseguradora"}</span>
                  <span className={`inline-flex items-center gap-1 font-semibold ${overdue ? "text-rose-600" : soon ? "text-amber-600" : "text-gray-700"}`}>
                    {overdue && <AlertTriangle className="h-3.5 w-3.5" />}
                    {l.poliza_vence ? fmtDate(l.poliza_vence) : "—"}
                    {d !== null && <span className="font-normal text-gray-500">({overdue ? `hace ${Math.abs(d)}d` : `en ${d}d`})</span>}
                  </span>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/file?path=${encodeURIComponent(l.poliza.path)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`policy-view-${l.id}-m`}
                  className="mt-3 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-gray-200 text-xs font-semibold text-brand-ink"
                >
                  <FileText className="h-4 w-4" /> Ver póliza
                </a>
              </div>
            );
          })}
        </div>

        {/* Desktop: tabla */}
        <div className="hidden overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-sm md:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Industria</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Aseguradora</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Vencimiento</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Póliza</th>
                {EMAILS_ENABLED && <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((l) => {
                const d = daysUntil(l.poliza_vence);
                const soon = d !== null && d <= 30;
                const overdue = d !== null && d < 0;
                return (
                  <tr key={l.id} data-testid={`policy-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-brand-ink">{l.nombre}</div>
                      <div className="text-xs text-gray-500">{l.rut} · {l.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(l.tipo_industria || []).map((k) => (
                          <span key={k} className="inline-flex items-center rounded-full bg-brand-yellow/15 text-brand-ink px-2 py-0.5 text-[10px] font-semibold">
                            {INDUSTRIA_LABELS[k] || k}
                          </span>
                        ))}
                      </div>
                      {l.equipo_descripcion && <div className="text-[11px] text-gray-500 mt-1 max-w-xs truncate" title={l.equipo_descripcion}>{l.equipo_descripcion}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{l.aseguradora || "-"}</td>
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center gap-1 text-xs font-semibold ${overdue ? "text-rose-600" : soon ? "text-amber-600" : "text-gray-700"}`}>
                        {overdue && <AlertTriangle className="h-3.5 w-3.5" />}
                        {l.poliza_vence ? fmtDate(l.poliza_vence) : "—"}
                        {d !== null && <span className="text-[10px] font-normal text-gray-500">({overdue ? `hace ${Math.abs(d)}d` : `en ${d}d`})</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/file?path=${encodeURIComponent(l.poliza.path)}`} target="_blank" rel="noreferrer" data-testid={`policy-view-${l.id}`} className="inline-flex items-center gap-1 text-xs text-brand-ink hover:text-brand-gold">
                        <FileText className="h-3.5 w-3.5" /> {l.poliza.filename}
                      </a>
                    </td>
                    {EMAILS_ENABLED && (
                      <td className="px-4 py-3">
                        <Button size="sm" variant="outline" onClick={() => sendPoliza(l)} disabled={!!sending[l.id]} data-testid={`policy-send-${l.id}`}>
                          {sending[l.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                          <span className="ml-1 text-xs">{l.poliza_enviada_at ? "Reenviar" : "Enviar"}</span>
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
